import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'hooks/theme';
import { AuthContext } from '@repo-level/core-front';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

import { TabBarContext } from 'contexts/tabBarSize';
import { TooltipTypes, useGlobalTooltipStore } from '../store';
import { ButtonCross } from 'components/buttons';

import { ScTooltipGlobal } from 'components/tooltip/styled';

const TOOLTIP_REMOVE_TIMEOUT = 1e4; // 10 seconds

export const GlobalTooltip: FC = () => {
  const { t } = useTranslation();

  const tooltip = useGlobalTooltipStore((state) => state.tooltip);
  const remove = useGlobalTooltipStore((state) => state.remove);

  const {
    successIcon: SuccessIcon,
    infoIcon: InfoIcon,
    warningIcon: WarningIcon,
    errorIcon: ErrorIcon,
  } = useTheme();

  const { isAuthenticated } = useContext(AuthContext);

  const { tabBarHeight } = useContext(TabBarContext);

  const [timerSetTimeout, setTimerSetTimeout] = useState<NodeJS.Timeout>();

  const startRemovingProcess = () => {
    const timeout = setTimeout(() => {
      remove();
    }, TOOLTIP_REMOVE_TIMEOUT);

    setTimerSetTimeout(timeout);
  };

  const stopRemovingProcess = () => {
    if (timerSetTimeout == null) return;

    clearTimeout(timerSetTimeout);
    setTimerSetTimeout(undefined);
  };

  useEffect(() => {
    if (tooltip && tooltip.animate) {
      startRemovingProcess();

      return () => {
        stopRemovingProcess();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tooltip]);

  const { title, text, icon } = useMemo(() => {
    let title, text;

    if (tooltip == null)
      return { title: undefined, text: undefined, icon: undefined };

    if (tooltip.translate) {
      if (typeof tooltip.title === 'function') {
        title = tooltip.title(t);
      } else if (typeof tooltip.title === 'string') {
        title = t(tooltip.title);
      }
    } else {
      title = tooltip.title;
    }

    if (tooltip.translate) {
      if (typeof tooltip.text === 'function') {
        text = tooltip.text(t);
      } else if (typeof tooltip.text === 'string') {
        text = t(tooltip.text);
      }
    } else {
      text = tooltip.text;
    }

    const icon = {
      [TooltipTypes.SUCCESS]: <SuccessIcon />,
      [TooltipTypes.INFO]: <InfoIcon />,
      [TooltipTypes.WARNING]: <WarningIcon />,
      [TooltipTypes.ERROR]: <ErrorIcon />,
    }[tooltip.type];

    return { title, text, icon };
  }, [tooltip, t, SuccessIcon, InfoIcon, WarningIcon, ErrorIcon]);

  return (
    <AnimatePresence>
      {tooltip != null && (
        <ScTooltipGlobal
          isAuthenticated={isAuthenticated}
          key={tooltip.id}
          success={tooltip.type === TooltipTypes.SUCCESS}
          warning={tooltip.type === TooltipTypes.WARNING}
          info={tooltip.type === TooltipTypes.INFO}
          error={tooltip.type === TooltipTypes.ERROR}
          onClick={remove}
          onMouseEnter={stopRemovingProcess}
          onMouseLeave={startRemovingProcess}
          as={motion.div}
          transition={{ duration: 0.3, delay: 0.2 }}
          initial={{ opacity: 0, x: '120%', transform: 'none' }}
          animate={{ opacity: 1, x: 0, transform: 'none' }}
          exit={{ opacity: 0, x: '120%', transform: 'none' }}
          bottom={tabBarHeight}
        >
          <ButtonCross top={8} right={8} onClick={remove} small />
          <div className="icon">{tooltip.icon ?? icon}</div>
          <div className="title">{title}</div>
          <div className="text">{text}</div>
          {tooltip.button && <div className="button">{tooltip.button}</div>}
        </ScTooltipGlobal>
      )}
    </AnimatePresence>
  );
};
